import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import CollapseCard from "../../../Home/components/CollapseCard";
import ColumnComponents from "../../../Home/components/ColumnComponents";
import HiddenGroups from "../../../Home/components/ColumnComponents/HiddenGroups";
import {
  selectGroupingLoader,
  selectGroupingTicketsLoader,
  selectIsSubgroupCollapsed,
  selectTicketGroups,
} from "../../redux/selector";
import { BoardViewType } from "app/types";
import { TicketGroup } from "../../types";
import Loading from "./Loading";
import { KanbanSubgroupLayout } from "./KanbanSubgroupLayout";

export default function Kanban() {
  const groups = useSelector(selectTicketGroups);
  const groupLoading = useSelector(selectGroupingLoader);
  const groupTicketsLoading = useSelector(selectGroupingTicketsLoader);
  const isSubgroupCollapsed = useSelector(selectIsSubgroupCollapsed);

  const getSubgroupKey = (id: string | string[]) => {
    if (Array.isArray(id)) {
      return Array.from(new Set(id)).sort().join(",");
    }
    return id;
  };

  const visibleGroups = useMemo(
    () =>
      [...groups]
        .sort((a, b) => a.order - b.order)
        .filter((g) => !g.isCollapse && g.isVisible),
    [groups],
  );

  const subGroupStatsMap = useMemo(() => {
    const map: Record<string, { count: number; limit: number | undefined }> =
      {};
    visibleGroups.forEach((group) => {
      group.subGroups?.forEach((sub) => {
        const key = getSubgroupKey(sub._id);
        if (!map[key]) {
          map[key] = {
            count: sub.ticketsList?.length || 0,
            limit: sub.ticketLimit,
          };
        } else {
          map[key].count += sub.ticketsList?.length || 0;
          map[key].limit = (map[key].limit || 0) + (sub.ticketLimit || 0);
        }
      });
    });
    return map;
  }, [visibleGroups]);

  const subGroups = useMemo(() => {
    const seen = new Set<string>();
    const result: TicketGroup[] = [];
    visibleGroups.forEach((group) => {
      group.subGroups?.forEach((sub) => {
        const key = getSubgroupKey(sub._id);
        if (!seen.has(key)) {
          seen.add(key);
          result.push(sub);
        }
      });
    });
    return result;
  }, [visibleGroups]);

  const hasSubGroups = subGroups.length > 0;

  if (groupLoading) {
    return (
      <div className="flex-shrink-0 w-full h-[calc(100vh-160px)] px-[24px]">
        <Loading />
      </div>
    );
  }

  if (!hasSubGroups) {
    return (
      <div className="flex-shrink-0 w-full min-w-0 h-[calc(100vh-160px)] overflow-x-auto px-[24px]">
        <div className="w-full min-w-0 h-full flex gap-[16px]">
          <CollapseCard />
          <div className="flex gap-[16px] h-full">
            {visibleGroups.map((group, index) => (
              <div key={group._id} className="w-[255px]">
                <ColumnComponents
                  group={group}
                  groupLoading={groupLoading}
                  groupTicketsLoading={groupTicketsLoading}
                  renderOnlyHeader={false}
                />
              </div>
            ))}
          </div>
          <HiddenGroups type={BoardViewType.KANBAN} />
        </div>
      </div>
    );
  }

  return (
    <KanbanSubgroupLayout
      visibleGroups={visibleGroups}
      subGroups={subGroups}
      subGroupStatsMap={subGroupStatsMap}
      isSubgroupCollapsed={isSubgroupCollapsed}
      groupLoading={groupLoading}
      getSubgroupKey={getSubgroupKey}
    />
  );
}
